import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  FileSearch,
  FileText,
  FileUp,
  HelpCircle,
  Link2,
  Plus,
  Search,
  TrendingDown,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/core/LanguageContext";
import { useAuth } from "@/core/AuthContext";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Textarea } from "@/shared/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import {
  createSupplierIncomingResult,
  createSupplierIncomingTemplate,
  fetchSupplierIncomingResultsByFactory,
  fetchSupplierIncomingTemplatesByFactory,
  type SupplierIncomingDocumentType,
  type SupplierIncomingInspectionStatus,
  type SupplierIncomingResult,
  type SupplierIncomingTemplateWithRelations,
} from "../api";
import { getStatusBadge } from "./InspectionDetailDialog";
import type { Supplier } from "../api";

interface IncomingTemplateFormData {
  name: string;
  description: string;
  supplierSelfService: boolean;
  assignedSupplierId: string | null;
}

interface ManualInspectionFormData {
  templateId: string | null;
  documentType: SupplierIncomingDocumentType;
  materialName: string;
  batchLotNumber: string;
  supplierId: string | null;
  supplierName: string;
  documentDate: string;
  expiryDate: string;
  overallStatus: SupplierIncomingInspectionStatus;
  inspectorNotes: string;
}

const emptyTemplateForm: IncomingTemplateFormData = {
  name: "",
  description: "",
  supplierSelfService: false,
  assignedSupplierId: null,
};

const emptyManualForm: ManualInspectionFormData = {
  templateId: null,
  documentType: "coa",
  materialName: "",
  batchLotNumber: "",
  supplierId: null,
  supplierName: "",
  documentDate: "",
  expiryDate: "",
  overallStatus: "pending",
  inspectorNotes: "",
};

interface InspectionsTabProps {
  suppliers: Supplier[];
  tenantId: string;
  factoryId: string;
  onViewInspection: (inspection: SupplierIncomingResult) => void;
}

export function InspectionsTab({
  suppliers,
  tenantId,
  factoryId,
  onViewInspection,
}: InspectionsTabProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [inspectionSearch, setInspectionSearch] = useState("");
  const [inspectionStatusFilter, setInspectionStatusFilter] = useState("all");
  const [inspectionSupplierFilter, setInspectionSupplierFilter] = useState("all");
  const [inspectionSortBy, setInspectionSortBy] = useState<"date" | "material" | "supplier">(
    "date",
  );
  const [inspectionSortOrder, setInspectionSortOrder] = useState<"asc" | "desc">("desc");

  const [portalLinksDialogOpen, setPortalLinksDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateFormData, setTemplateFormData] =
    useState<IncomingTemplateFormData>(emptyTemplateForm);

  const [manualInspectionDialogOpen, setManualInspectionDialogOpen] = useState(false);
  const [manualInspectionForm, setManualInspectionForm] =
    useState<ManualInspectionFormData>(emptyManualForm);
  const [manualInspectionFile, setManualInspectionFile] = useState<File | null>(null);

  // Query keys
  const incomingTemplateQueryKey = [
    "mod_supplier_incoming",
    "templates",
    tenantId,
    factoryId,
  ] as const;

  const allInspectionsQueryKey = [
    "mod_supplier_incoming",
    "all-inspection-results",
    tenantId,
    factoryId,
  ] as const;

  const { data: incomingTemplates = [], isLoading: isLoadingIncomingTemplates } = useQuery({
    queryKey: incomingTemplateQueryKey,
    queryFn: () => fetchSupplierIncomingTemplatesByFactory(tenantId, factoryId),
    enabled: !!tenantId && !!factoryId,
  });

  const { data: allInspections = [], isLoading: isLoadingAllInspections } = useQuery({
    queryKey: allInspectionsQueryKey,
    queryFn: () => fetchSupplierIncomingResultsByFactory(tenantId, factoryId),
    enabled: !!tenantId && !!factoryId,
  });

  const createTemplateMutation = useMutation({
    mutationFn: async () => {
      const name = templateFormData.name.trim();
      if (!name) {
        throw new Error(
          t(
            "mod_8d.supplierQuality.incomingInspection.validation.nameRequired",
            "Template name is required",
          ),
        );
      }
      await createSupplierIncomingTemplate({
        tenant_id: tenantId,
        factory_id: factoryId,
        name,
        description: templateFormData.description.trim() || null,
        supplier_self_service: templateFormData.supplierSelfService,
        assigned_supplier_id: templateFormData.supplierSelfService
          ? templateFormData.assignedSupplierId
          : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incomingTemplateQueryKey });
      toast({
        title: t(
          "mod_8d.supplierQuality.incomingInspection.toast.created",
          "Incoming inspection template created",
        ),
      });
      setTemplateDialogOpen(false);
      setTemplateFormData(emptyTemplateForm);
    },
    onError: (error: Error) => {
      toast({
        title: t("mod_8d.supplierQuality.toast.errorTitle", "Error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createManualInspectionMutation = useMutation({
    mutationFn: async () => {
      if (!manualInspectionForm.templateId) {
        throw new Error(
          t(
            "mod_8d.supplierQuality.incomingInspection.validation.templateRequired",
            "Template selection is required",
          ),
        );
      }
      if (!manualInspectionFile) {
        throw new Error(
          t(
            "mod_8d.supplierQuality.incomingInspection.validation.documentRequired",
            "Inspection document is required",
          ),
        );
      }

      const fileExt = manualInspectionFile.name.split(".").pop() ?? "pdf";
      const filePath = `manual/${factoryId}/${manualInspectionForm.templateId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("incoming-inspection-docs")
        .upload(filePath, manualInspectionFile);

      if (uploadError) throw uploadError;

      await createSupplierIncomingResult({
        tenant_id: tenantId,
        factory_id: factoryId,
        template_id: manualInspectionForm.templateId,
        submitted_by: user?.id ?? null,
        document_type: manualInspectionForm.documentType,
        document_file_path: filePath,
        document_file_name: manualInspectionFile.name,
        material_name: manualInspectionForm.materialName.trim() || null,
        batch_lot_number: manualInspectionForm.batchLotNumber.trim() || null,
        supplier_id: manualInspectionForm.supplierId,
        supplier_name: manualInspectionForm.supplierName.trim() || null,
        document_date: manualInspectionForm.documentDate || null,
        expiry_date: manualInspectionForm.expiryDate || null,
        test_results: [],
        overall_status: manualInspectionForm.overallStatus,
        inspector_notes: manualInspectionForm.inspectorNotes.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: allInspectionsQueryKey });
      queryClient.invalidateQueries({ queryKey: incomingTemplateQueryKey });
      // Also invalidate the time-filtered inspections used by overview/SPC tabs
      queryClient.invalidateQueries({
        queryKey: ["mod_supplier_incoming", "inspection-results", tenantId, factoryId],
        exact: false,
      });
      toast({
        title: t(
          "mod_8d.supplierQuality.incomingInspection.toast.manualCreated",
          "Incoming inspection added",
        ),
      });
      setManualInspectionDialogOpen(false);
      setManualInspectionForm(emptyManualForm);
      setManualInspectionFile(null);
    },
    onError: (error: Error) => {
      toast({
        title: t("mod_8d.supplierQuality.toast.errorTitle", "Error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const getSupplierPortalLink = (template: SupplierIncomingTemplateWithRelations) => {
    if (!template.supplier_magic_token) return null;
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://app.factory-pilot.ai";
    return `${origin}/supplier-inspection/${template.supplier_magic_token}`;
  };

  const copySupplierPortalLink = async (template: SupplierIncomingTemplateWithRelations) => {
    const link = getSupplierPortalLink(template);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: t(
          "mod_8d.supplierQuality.incomingInspection.toast.linkCopied",
          "Supplier portal link copied",
        ),
      });
    } catch {
      toast({
        title: t("mod_8d.supplierQuality.toast.errorTitle", "Error"),
        description: t(
          "mod_8d.supplierQuality.incomingInspection.toast.copyFailed",
          "Failed to copy supplier portal link",
        ),
        variant: "destructive",
      });
    }
  };

  const manualTemplateOptions = incomingTemplates.filter((t) => t.is_active);

  const openManualInspectionDialog = () => {
    setManualInspectionForm({
      ...emptyManualForm,
      templateId: manualTemplateOptions[0]?.id ?? null,
      supplierId: manualTemplateOptions[0]?.assigned_supplier?.id ?? null,
      supplierName: manualTemplateOptions[0]?.assigned_supplier?.name ?? "",
    });
    setManualInspectionFile(null);
    setManualInspectionDialogOpen(true);
  };

  // Filter & sort
  const filteredInspections = allInspections
    .filter((inspection) => {
      if (inspectionSearch.trim()) {
        const searchLower = inspectionSearch.toLowerCase();
        const matchesMaterial = inspection.material_name?.toLowerCase().includes(searchLower);
        const matchesBatch = inspection.batch_lot_number?.toLowerCase().includes(searchLower);
        if (!matchesMaterial && !matchesBatch) return false;
      }
      if (inspectionStatusFilter !== "all" && inspection.overall_status !== inspectionStatusFilter)
        return false;
      if (inspectionSupplierFilter !== "all" && inspection.supplier_id !== inspectionSupplierFilter)
        return false;
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (inspectionSortBy) {
        case "date":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case "material":
          comparison = (a.material_name || "").localeCompare(b.material_name || "");
          break;
        case "supplier":
          comparison = (a.supplier_name || "").localeCompare(b.supplier_name || "");
          break;
      }
      return inspectionSortOrder === "asc" ? comparison : -comparison;
    });

  const activeInspectionFiltersCount = [
    inspectionSearch.trim() ? 1 : 0,
    inspectionStatusFilter !== "all" ? 1 : 0,
    inspectionSupplierFilter !== "all" ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearInspectionFilters = () => {
    setInspectionSearch("");
    setInspectionStatusFilter("all");
    setInspectionSupplierFilter("all");
  };

  const handleSortBy = (col: "date" | "material" | "supplier") => {
    if (inspectionSortBy === col) {
      setInspectionSortOrder(inspectionSortOrder === "asc" ? "desc" : "asc");
    } else {
      setInspectionSortBy(col);
      setInspectionSortOrder(col === "date" ? "desc" : "asc");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header card with action buttons */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>
              {t(
                "mod_8d.supplierQuality.incomingInspection.instances.title",
                "Incoming Inspections",
              )}
            </CardTitle>
            <CardDescription>
              {t(
                "mod_8d.supplierQuality.incomingInspection.instances.description",
                "Log inspections manually or review supplier-submitted certificates.",
              )}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Portal Links Dialog */}
            <Dialog open={portalLinksDialogOpen} onOpenChange={setPortalLinksDialogOpen}>
              <DialogTrigger asChild>
                <Button type="button" variant="outline" className="gap-2">
                  <Link2 className="h-4 w-4" />
                  {t(
                    "mod_8d.supplierQuality.incomingInspection.actions.managePortalLinks",
                    "Manage portal links",
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>
                    {t(
                      "mod_8d.supplierQuality.incomingInspection.portalDialog.title",
                      "Supplier portal templates",
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      "mod_8d.supplierQuality.incomingInspection.portalDialog.description",
                      "Templates define who can upload certificates and generate portal links.",
                    )}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Create Template nested dialog */}
                    <Dialog
                      open={templateDialogOpen}
                      onOpenChange={(open) => {
                        setTemplateDialogOpen(open);
                        if (!open) setTemplateFormData(emptyTemplateForm);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button type="button" className="gap-2">
                          <Plus className="h-4 w-4" />
                          {t(
                            "mod_8d.supplierQuality.incomingInspection.actions.create",
                            "Create template",
                          )}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.dialog.title",
                              "Create Incoming Inspection Template",
                            )}
                          </DialogTitle>
                          <DialogDescription>
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.dialog.subtitle",
                              "Templates are required to generate supplier upload links and keep inspections organized.",
                            )}
                          </DialogDescription>
                        </DialogHeader>

                        <form
                          className="space-y-4"
                          onSubmit={(event) => {
                            event.preventDefault();
                            createTemplateMutation.mutate();
                          }}
                        >
                          <div className="space-y-2">
                            <Label htmlFor="incoming-template-name">
                              {t(
                                "mod_8d.supplierQuality.incomingInspection.form.name",
                                "Template Name *",
                              )}
                            </Label>
                            <Input
                              id="incoming-template-name"
                              value={templateFormData.name}
                              onChange={(event) =>
                                setTemplateFormData((prev) => ({
                                  ...prev,
                                  name: event.target.value,
                                }))
                              }
                              placeholder={t(
                                "mod_8d.supplierQuality.incomingInspection.form.namePlaceholder",
                                "e.g. Resin Incoming Inspection",
                              )}
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="incoming-template-description">
                              {t(
                                "mod_8d.supplierQuality.incomingInspection.form.description",
                                "Description",
                              )}
                            </Label>
                            <Input
                              id="incoming-template-description"
                              value={templateFormData.description}
                              onChange={(event) =>
                                setTemplateFormData((prev) => ({
                                  ...prev,
                                  description: event.target.value,
                                }))
                              }
                              placeholder={t(
                                "mod_8d.supplierQuality.incomingInspection.form.descriptionPlaceholder",
                                "Short description of this inspection template",
                              )}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor="incoming-template-self-service">
                                  {t(
                                    "mod_8d.supplierQuality.incomingInspection.form.enableSupplierPortal",
                                    "Enable Supplier Portal",
                                  )}
                                </Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                    >
                                      <HelpCircle className="h-4 w-4" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent align="start">
                                    <p className="text-sm">
                                      {t(
                                        "mod_8d.supplierQuality.incomingInspection.form.enableSupplierPortalHelp",
                                        "Turn this on to generate a secure link for suppliers to upload CoA/CoC documents. Manual inspections can still be logged by supervisors.",
                                      )}
                                    </p>
                                  </PopoverContent>
                                </Popover>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {t(
                                  "mod_8d.supplierQuality.incomingInspection.form.enableSupplierPortalDescription",
                                  "Generate a magic link so suppliers can upload certificates directly.",
                                )}
                              </p>
                            </div>
                            <Switch
                              id="incoming-template-self-service"
                              checked={templateFormData.supplierSelfService}
                              onCheckedChange={(checked) =>
                                setTemplateFormData((prev) => ({
                                  ...prev,
                                  supplierSelfService: checked,
                                  assignedSupplierId: checked ? prev.assignedSupplierId : null,
                                }))
                              }
                            />
                          </div>

                          {templateFormData.supplierSelfService && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label htmlFor="incoming-template-supplier">
                                  {t(
                                    "mod_8d.supplierQuality.incomingInspection.form.assignedSupplier",
                                    "Assigned Supplier",
                                  )}
                                </Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                    >
                                      <HelpCircle className="h-4 w-4" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent align="start">
                                    <p className="text-sm">
                                      {t(
                                        "mod_8d.supplierQuality.incomingInspection.form.assignedSupplierHelp",
                                        "Link templates to a supplier so their team can use the portal link. Leave blank for internal inspections only.",
                                      )}
                                    </p>
                                  </PopoverContent>
                                </Popover>
                              </div>
                              <Select
                                value={templateFormData.assignedSupplierId ?? "none"}
                                onValueChange={(value) =>
                                  setTemplateFormData((prev) => ({
                                    ...prev,
                                    assignedSupplierId: value === "none" ? null : value,
                                  }))
                                }
                              >
                                <SelectTrigger id="incoming-template-supplier">
                                  <SelectValue
                                    placeholder={t(
                                      "mod_8d.supplierQuality.incomingInspection.form.assignedSupplierPlaceholder",
                                      "Select supplier",
                                    )}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    {t(
                                      "mod_8d.supplierQuality.incomingInspection.form.noSupplier",
                                      "No supplier selected",
                                    )}
                                  </SelectItem>
                                  {suppliers.map((supplier) => (
                                    <SelectItem key={supplier.id} value={supplier.id}>
                                      {supplier.code} - {supplier.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setTemplateDialogOpen(false);
                                setTemplateFormData(emptyTemplateForm);
                              }}
                            >
                              {t("mod_8d.supplierQuality.actions.cancel", "Cancel")}
                            </Button>
                            <Button type="submit" disabled={createTemplateMutation.isPending}>
                              {t(
                                "mod_8d.supplierQuality.incomingInspection.actions.create",
                                "Create template",
                              )}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8">
                          <HelpCircle className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start">
                        <p className="text-sm">
                          {t(
                            "mod_8d.supplierQuality.incomingInspection.portalDialog.help",
                            "Templates define the incoming inspection flow and generate secure portal links for suppliers. Keep the main list clean by managing templates here.",
                          )}
                        </p>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-3">
                    {isLoadingIncomingTemplates ? (
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.templates.loading",
                          "Loading incoming inspection templates...",
                        )}
                      </p>
                    ) : incomingTemplates.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.templates.empty",
                          "No incoming inspection templates yet. Create your first template above.",
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {incomingTemplates.map((template) => {
                          const portalLink = getSupplierPortalLink(template);
                          return (
                            <div
                              key={template.id}
                              className="rounded-lg border border-border p-3 sm:flex sm:items-center sm:justify-between"
                            >
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-medium text-sm">{template.name}</p>
                                  <Badge variant={template.is_active ? "default" : "outline"}>
                                    {template.is_active
                                      ? t(
                                          "mod_8d.supplierQuality.incomingInspection.templates.active",
                                          "Active",
                                        )
                                      : t(
                                          "mod_8d.supplierQuality.incomingInspection.templates.inactive",
                                          "Inactive",
                                        )}
                                  </Badge>
                                  {template.supplier_self_service && (
                                    <Badge variant="secondary">
                                      {t(
                                        "mod_8d.supplierQuality.incomingInspection.templates.portalEnabled",
                                        "Supplier Portal Enabled",
                                      )}
                                    </Badge>
                                  )}
                                </div>
                                {template.description && (
                                  <p className="text-xs text-muted-foreground">
                                    {template.description}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {template.assigned_supplier?.name
                                    ? t(
                                        "mod_8d.supplierQuality.incomingInspection.templates.assignedSupplier",
                                        `Assigned supplier: ${template.assigned_supplier.name}`,
                                      )
                                    : t(
                                        "mod_8d.supplierQuality.incomingInspection.templates.unassignedSupplier",
                                        "No supplier assigned",
                                      )}
                                </p>
                              </div>
                              {portalLink && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-3 gap-2 sm:mt-0"
                                  onClick={() => copySupplierPortalLink(template)}
                                >
                                  <Copy className="h-4 w-4" />
                                  {t(
                                    "mod_8d.supplierQuality.incomingInspection.actions.copyPortalLink",
                                    "Copy Portal Link",
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Manual Inspection Dialog */}
            <Dialog
              open={manualInspectionDialogOpen}
              onOpenChange={(open) => {
                setManualInspectionDialogOpen(open);
                if (!open) {
                  setManualInspectionForm(emptyManualForm);
                  setManualInspectionFile(null);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button type="button" className="gap-2" onClick={openManualInspectionDialog}>
                  <FileUp className="h-4 w-4" />
                  {t(
                    "mod_8d.supplierQuality.incomingInspection.actions.addManual",
                    "Add incoming inspection",
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>
                    {t(
                      "mod_8d.supplierQuality.incomingInspection.manual.title",
                      "Log incoming inspection",
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      "mod_8d.supplierQuality.incomingInspection.manual.description",
                      "Capture an inspection from a supplier or internal review and attach the certificate.",
                    )}
                  </DialogDescription>
                </DialogHeader>
                <form
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    createManualInspectionMutation.mutate();
                  }}
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="manual-template">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.template",
                          "Template *",
                        )}
                      </Label>
                      <Select
                        value={manualInspectionForm.templateId ?? ""}
                        onValueChange={(value) => {
                          const tmpl = incomingTemplates.find((item) => item.id === value);
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            templateId: value,
                            supplierId: tmpl?.assigned_supplier?.id ?? null,
                            supplierName: tmpl?.assigned_supplier?.name ?? "",
                          }));
                        }}
                      >
                        <SelectTrigger id="manual-template">
                          <SelectValue
                            placeholder={t(
                              "mod_8d.supplierQuality.incomingInspection.manual.templatePlaceholder",
                              "Select template",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {incomingTemplates.map((tmpl) => (
                            <SelectItem key={tmpl.id} value={tmpl.id}>
                              {tmpl.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {incomingTemplates.length === 0 && (
                      <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground md:col-span-2">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.noTemplates",
                          "Create a template first to log manual inspections.",
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="manual-document-type">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.documentType",
                          "Document Type",
                        )}
                      </Label>
                      <Select
                        value={manualInspectionForm.documentType}
                        onValueChange={(value) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            documentType: value as SupplierIncomingDocumentType,
                          }))
                        }
                      >
                        <SelectTrigger id="manual-document-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="coa">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.documentType.coa",
                              "CoA",
                            )}
                          </SelectItem>
                          <SelectItem value="coc">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.documentType.coc",
                              "CoC",
                            )}
                          </SelectItem>
                          <SelectItem value="other">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.documentType.other",
                              "Other",
                            )}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-material">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.material",
                          "Material Name",
                        )}
                      </Label>
                      <Input
                        id="manual-material"
                        value={manualInspectionForm.materialName}
                        onChange={(event) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            materialName: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-batch">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.batchLot",
                          "Batch / Lot",
                        )}
                      </Label>
                      <Input
                        id="manual-batch"
                        value={manualInspectionForm.batchLotNumber}
                        onChange={(event) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            batchLotNumber: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-supplier">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.supplier",
                          "Supplier",
                        )}
                      </Label>
                      <Select
                        value={manualInspectionForm.supplierId ?? "none"}
                        onValueChange={(value) => {
                          const supplier = suppliers.find((s) => s.id === value);
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            supplierId: value === "none" ? null : value,
                            supplierName: supplier?.name ?? "",
                          }));
                        }}
                      >
                        <SelectTrigger id="manual-supplier">
                          <SelectValue
                            placeholder={t(
                              "mod_8d.supplierQuality.incomingInspection.manual.supplierPlaceholder",
                              "Select supplier",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.supplierNone",
                              "No supplier selected",
                            )}
                          </SelectItem>
                          {suppliers.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.code} - {supplier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-status">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.status",
                          "Status",
                        )}
                      </Label>
                      <Select
                        value={manualInspectionForm.overallStatus}
                        onValueChange={(value) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            overallStatus: value as SupplierIncomingInspectionStatus,
                          }))
                        }
                      >
                        <SelectTrigger id="manual-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.status.pending",
                              "Pending",
                            )}
                          </SelectItem>
                          <SelectItem value="approved">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.status.approved",
                              "Approved",
                            )}
                          </SelectItem>
                          <SelectItem value="rejected">
                            {t(
                              "mod_8d.supplierQuality.incomingInspection.manual.status.rejected",
                              "Rejected",
                            )}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-document-date">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.documentDate",
                          "Document Date",
                        )}
                      </Label>
                      <Input
                        id="manual-document-date"
                        type="date"
                        value={manualInspectionForm.documentDate}
                        onChange={(event) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            documentDate: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual-expiry-date">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.expiryDate",
                          "Expiry Date",
                        )}
                      </Label>
                      <Input
                        id="manual-expiry-date"
                        type="date"
                        value={manualInspectionForm.expiryDate}
                        onChange={(event) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            expiryDate: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="manual-document">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.document",
                          "Inspection Document *",
                        )}
                      </Label>
                      <Input
                        id="manual-document"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        required
                        onChange={(event) => {
                          setManualInspectionFile(event.target.files?.[0] ?? null);
                        }}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="manual-notes">
                        {t(
                          "mod_8d.supplierQuality.incomingInspection.manual.notes",
                          "Inspector Notes",
                        )}
                      </Label>
                      <Textarea
                        id="manual-notes"
                        rows={3}
                        value={manualInspectionForm.inspectorNotes}
                        onChange={(event) =>
                          setManualInspectionForm((prev) => ({
                            ...prev,
                            inspectorNotes: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setManualInspectionDialogOpen(false);
                        setManualInspectionForm(emptyManualForm);
                        setManualInspectionFile(null);
                      }}
                    >
                      {t("mod_8d.supplierQuality.actions.cancel", "Cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createManualInspectionMutation.isPending ||
                        incomingTemplates.length === 0 ||
                        !manualInspectionForm.templateId ||
                        !manualInspectionFile
                      }
                    >
                      {t(
                        "mod_8d.supplierQuality.incomingInspection.actions.addManual",
                        "Add incoming inspection",
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-start sm:items-center">
        <div className="relative flex-1 min-w-[250px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search material or batch/lot..."
            value={inspectionSearch}
            onChange={(e) => setInspectionSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={inspectionStatusFilter} onValueChange={setInspectionStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>

        <Select value={inspectionSupplierFilter} onValueChange={setInspectionSupplierFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suppliers</SelectItem>
            {suppliers.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {activeInspectionFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearInspectionFilters}>
            <XCircle className="h-4 w-4 mr-2" />
            Clear
          </Button>
        )}

        <div className="ml-auto text-sm text-muted-foreground flex items-center">
          {filteredInspections.length}{" "}
          {filteredInspections.length === 1 ? "inspection" : "inspections"}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoadingAllInspections ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading inspections...</p>
              </div>
            </div>
          ) : filteredInspections.length === 0 ? (
            <div className="py-12 text-center">
              <FileSearch className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
              <p className="text-muted-foreground">
                {activeInspectionFiltersCount > 0
                  ? "No inspections match your filters"
                  : "No incoming inspections recorded yet"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="bg-muted/50">
                    <th
                      className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSortBy("date")}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {inspectionSortBy === "date" &&
                          (inspectionSortOrder === "asc" ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSortBy("material")}
                    >
                      <div className="flex items-center gap-1">
                        Material
                        {inspectionSortBy === "material" &&
                          (inspectionSortOrder === "asc" ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Batch/Lot</th>
                    <th
                      className="px-4 py-3 text-left text-sm font-medium cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSortBy("supplier")}
                    >
                      <div className="flex items-center gap-1">
                        Supplier
                        {inspectionSortBy === "supplier" &&
                          (inspectionSortOrder === "asc" ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium hidden md:table-cell">
                      Doc Type
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections.map((inspection) => (
                    <tr
                      key={inspection.id}
                      className="border-b hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium">
                        {format(new Date(inspection.created_at), "MMM dd, yyyy")}
                      </td>
                      <td className="px-4 py-3 text-sm">{inspection.material_name || "-"}</td>
                      <td className="px-4 py-3 text-sm">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {inspection.batch_lot_number || "-"}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm">{inspection.supplier_name || "-"}</td>
                      <td className="px-4 py-3 text-sm">
                        {getStatusBadge(inspection.overall_status)}
                      </td>
                      <td className="px-4 py-3 text-sm hidden md:table-cell">
                        <Badge variant="outline" className="text-xs">
                          {inspection.document_type?.toUpperCase() || "N/A"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8"
                          onClick={() => onViewInspection(inspection)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Details</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
